import { Box, Typography, TextField } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import * as React from 'react';
import { useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { vinDecoderPageStyles } from './styles';

interface SectionConfig {
  title: string;
  fields: { key: string; label: string }[];
}

// NHTSA vPIC decoded fields grouped into display sections.
// Keys match the API response fields from decodevinvalues endpoint.
const SECTIONS: SectionConfig[] = [
  {
    title: 'Vehicle Identification',
    fields: [
      { key: 'VIN', label: 'VIN' },
      { key: 'VehicleDescriptor', label: 'Vehicle Descriptor' },
      { key: 'Make', label: 'Make' },
      { key: 'Model', label: 'Model' },
      { key: 'ModelYear', label: 'Model Year' },
      { key: 'Series', label: 'Series' },
      { key: 'Series2', label: 'Series 2' },
      { key: 'Trim', label: 'Trim' },
      { key: 'Trim2', label: 'Trim 2' },
      { key: 'VehicleType', label: 'Vehicle Type' },
      { key: 'BodyClass', label: 'Body Class' },
      { key: 'DestinationMarket', label: 'Destination Market' },
      { key: 'Note', label: 'Note' },
    ],
  },
  {
    title: 'Engine & Powertrain',
    fields: [
      { key: 'DisplacementL', label: 'Displacement (L)' },
      { key: 'DisplacementCC', label: 'Displacement (CC)' },
      { key: 'DisplacementCI', label: 'Displacement (CI)' },
      { key: 'EngineCylinders', label: 'Cylinders' },
      { key: 'EngineConfiguration', label: 'Configuration' },
      { key: 'EngineHP', label: 'Horsepower' },
      { key: 'EngineHP_to', label: 'Horsepower (to)' },
      { key: 'EngineKW', label: 'Power (kW)' },
      { key: 'EngineModel', label: 'Engine Model' },
      { key: 'EngineManufacturer', label: 'Engine Manufacturer' },
      { key: 'EngineCycles', label: 'Engine Cycles' },
      { key: 'FuelTypePrimary', label: 'Primary Fuel' },
      { key: 'FuelTypeSecondary', label: 'Secondary Fuel' },
      { key: 'FuelInjectionType', label: 'Fuel Injection' },
      { key: 'TransmissionStyle', label: 'Transmission' },
      { key: 'TransmissionSpeeds', label: 'Transmission Speeds' },
      { key: 'DriveType', label: 'Drive Type' },
      { key: 'ElectrificationLevel', label: 'Electrification Level' },
      { key: 'ValveTrainDesign', label: 'Valve Train' },
      { key: 'Turbo', label: 'Turbo' },
      { key: 'OtherEngineInfo', label: 'Other Engine Info' },
    ],
  },
  {
    title: 'Battery & Electric Vehicle',
    fields: [
      { key: 'BatteryType', label: 'Battery Type' },
      { key: 'BatteryKWh', label: 'Battery (kWh)' },
      { key: 'BatteryKWh_to', label: 'Battery kWh (to)' },
      { key: 'BatteryV', label: 'Battery Voltage' },
      { key: 'BatteryV_to', label: 'Battery Voltage (to)' },
      { key: 'BatteryA', label: 'Battery Amps' },
      { key: 'BatteryA_to', label: 'Battery Amps (to)' },
      { key: 'BatteryCells', label: 'Battery Cells' },
      { key: 'BatteryModules', label: 'Battery Modules' },
      { key: 'BatteryPacks', label: 'Battery Packs' },
      { key: 'BatteryInfo', label: 'Battery Info' },
      { key: 'ChargerLevel', label: 'Charger Level' },
      { key: 'ChargerPowerKW', label: 'Charger Power (kW)' },
      { key: 'EVDriveUnit', label: 'EV Drive Unit' },
    ],
  },
  {
    title: 'Safety',
    fields: [
      { key: 'ABS', label: 'ABS' },
      { key: 'ESC', label: 'ESC' },
      { key: 'TractionControl', label: 'Traction Control' },
      { key: 'AirBagLocFront', label: 'Front Airbags' },
      { key: 'AirBagLocSide', label: 'Side Airbags' },
      { key: 'AirBagLocCurtain', label: 'Curtain Airbags' },
      { key: 'AirBagLocKnee', label: 'Knee Airbags' },
      { key: 'AirBagLocSeatCushion', label: 'Seat Cushion Airbags' },
      { key: 'SeatBeltsAll', label: 'Seat Belts' },
      { key: 'Pretensioner', label: 'Pretensioner' },
      { key: 'OtherRestraintSystemInfo', label: 'Other Restraint Info' },
      { key: 'ActiveSafetySysNote', label: 'Active Safety Note' },
    ],
  },
  {
    title: 'Driver Assistance',
    fields: [
      { key: 'ForwardCollisionWarning', label: 'Forward Collision Warning' },
      { key: 'CIB', label: 'Crash Imminent Braking' },
      { key: 'BlindSpotMon', label: 'Blind Spot Monitoring' },
      { key: 'BlindSpotIntervention', label: 'Blind Spot Intervention' },
      { key: 'LaneDepartureWarning', label: 'Lane Departure Warning' },
      { key: 'LaneKeepSystem', label: 'Lane Keep System' },
      { key: 'LaneCenteringAssistance', label: 'Lane Centering Assistance' },
      { key: 'AdaptiveCruiseControl', label: 'Adaptive Cruise Control' },
      { key: 'AdaptiveHeadlights', label: 'Adaptive Headlights' },
      { key: 'AdaptiveDrivingBeam', label: 'Adaptive Driving Beam' },
      { key: 'DaytimeRunningLight', label: 'Daytime Running Lights' },
      { key: 'SemiautomaticHeadlampBeamSwitching', label: 'Auto Headlamp Beam Switching' },
      { key: 'AutoReverseSystem', label: 'Auto Reverse System' },
      { key: 'RearVisibilitySystem', label: 'Rear Visibility System' },
      { key: 'RearCrossTrafficAlert', label: 'Rear Cross Traffic Alert' },
      { key: 'RearAutomaticEmergencyBraking', label: 'Rear Auto Emergency Braking' },
      { key: 'PedestrianAutomaticEmergencyBraking', label: 'Pedestrian Auto Emergency Braking' },
      { key: 'ParkAssist', label: 'Park Assist' },
      { key: 'AutomaticPedestrianAlertingSound', label: 'Pedestrian Alerting Sound' },
      { key: 'KeylessIgnition', label: 'Keyless Ignition' },
      { key: 'DynamicBrakeSupport', label: 'Dynamic Brake Support' },
      { key: 'SAEAutomationLevel', label: 'SAE Automation Level' },
      { key: 'SAEAutomationLevel_to', label: 'SAE Automation Level (to)' },
      { key: 'WheelieMitigation', label: 'Wheelie Mitigation' },
    ],
  },
  {
    title: 'Dimensions & Weight',
    fields: [
      { key: 'CurbWeightLB', label: 'Curb Weight (lb)' },
      { key: 'GVWR', label: 'GVWR' },
      { key: 'GVWR_to', label: 'GVWR (to)' },
      { key: 'GCWR', label: 'GCWR' },
      { key: 'GCWR_to', label: 'GCWR (to)' },
      { key: 'Doors', label: 'Doors' },
      { key: 'SeatRows', label: 'Seat Rows' },
      { key: 'Seats', label: 'Seats' },
      { key: 'BedLengthIN', label: 'Bed Length (in)' },
      { key: 'BedType', label: 'Bed Type' },
      { key: 'WheelBaseShort', label: 'Wheelbase Short' },
      { key: 'WheelBaseLong', label: 'Wheelbase Long' },
      { key: 'WheelBaseType', label: 'Wheelbase Type' },
      { key: 'TrackWidth', label: 'Track Width' },
      { key: 'WheelSizeFront', label: 'Front Wheel Size' },
      { key: 'WheelSizeRear', label: 'Rear Wheel Size' },
      { key: 'Wheels', label: 'Wheels' },
      { key: 'Windows', label: 'Windows' },
    ],
  },
  {
    title: 'Manufacturing',
    fields: [
      { key: 'Manufacturer', label: 'Manufacturer' },
      { key: 'PlantCity', label: 'Plant City' },
      { key: 'PlantState', label: 'Plant State' },
      { key: 'PlantCountry', label: 'Plant Country' },
      { key: 'PlantCompanyName', label: 'Plant Company' },
    ],
  },
  {
    title: 'Brakes & Tires',
    fields: [
      { key: 'BrakeSystemType', label: 'Brake System Type' },
      { key: 'BrakeSystemDesc', label: 'Brake System Description' },
      { key: 'CombinedBrakingSystem', label: 'Combined Braking System' },
      { key: 'TPMS', label: 'TPMS' },
      { key: 'TopSpeedMPH', label: 'Top Speed (MPH)' },
    ],
  },
];

// Filter out empty or irrelevant values from the decoded results
const HIDDEN_VALUES = ['', 'Not Applicable'];

function isValueVisible(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  return !HIDDEN_VALUES.includes(String(value).trim());
}

function getVisibleFields(
  section: SectionConfig,
  data: Record<string, unknown>
): { key: string; label: string; value: string }[] {
  return section.fields.filter(f => isValueVisible(data[f.key])).map(f => ({ ...f, value: String(data[f.key]) }));
}

export default function VinDecoderPage() {
  const [vinInput, setVinInput] = useState('');
  const [decodeLoading, setDecodeLoading] = useState(false);
  const [decodeError, setDecodeError] = useState('');
  const [decodedData, setDecodedData] = useState<Record<string, unknown> | null>(null);

  const decodeVin = useCallback(() => {
    if (!vinInput) return;
    setDecodeLoading(true);
    setDecodeError('');
    setDecodedData(null);

    // NHTSA vPIC API — same endpoint used in VehicleDetailsSection
    const url = `https://vpic.nhtsa.dot.gov/api/vehicles/decodevinvalues/${vinInput}?format=json`;
    axios
      .get(url)
      .then(({ data }) => {
        const result = data?.Results?.[0];
        if (!result) {
          setDecodeError('Unable to decode VIN. Please try again.');
          return;
        }

        const hasUsableData = Boolean(result.Make || result.Model || result.ModelYear);
        if (!hasUsableData) {
          setDecodeError(result.ErrorText || 'Unable to decode VIN.');
          return;
        }

        setDecodedData(result);
      })
      .catch(e => setDecodeError(`${e}`))
      .finally(() => setDecodeLoading(false));
  }, [vinInput]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && vinInput) {
      decodeVin();
    }
  };

  const vehicleTitle = decodedData
    ? [decodedData.ModelYear, decodedData.Make, decodedData.Model, decodedData.Trim].filter(Boolean).join(' ').trim()
    : null;

  // Distribute sections across 3 columns, greedily assigning each to the
  // Shortest column so that the overall layout stays balanced in height.
  const columns = useMemo(() => {
    if (!decodedData) return [[], [], []] as SectionConfig[][];
    const visibleSections = SECTIONS.filter(s => getVisibleFields(s, decodedData).length > 0);
    const cols: SectionConfig[][] = [[], [], []];
    const colHeights = [0, 0, 0];
    for (const section of visibleSections) {
      const fieldCount = getVisibleFields(section, decodedData).length;
      const shortest = colHeights.indexOf(Math.min(...colHeights));
      cols[shortest].push(section);
      colHeights[shortest] += fieldCount + 2;
    }
    return cols;
  }, [decodedData]);

  return (
    <Box sx={vinDecoderPageStyles.container}>
      <Box sx={vinDecoderPageStyles.vinInputRow}>
        <TextField
          fullWidth
          variant="standard"
          size="small"
          placeholder="Enter a VIN number"
          value={vinInput}
          onChange={e => setVinInput(e.target.value)}
          onKeyDown={handleKeyDown}
          inputProps={{ sx: { ...vinDecoderPageStyles.detailTextFieldInput } }}
          sx={{ maxWidth: '400px' }}
        />
        <LoadingButton
          loading={decodeLoading}
          onClick={decodeVin}
          variant="contained"
          disabled={!vinInput}
          size="small"
        >
          Decode
        </LoadingButton>
      </Box>
      {decodeError ? (
        <Typography sx={{ marginBottom: '10px' }} color="error">
          {decodeError}
        </Typography>
      ) : null}

      {vehicleTitle ? (
        <Box sx={{ marginBottom: '20px' }}>
          <Typography sx={{ fontWeight: 'bold', fontSize: '18px' }}>{vehicleTitle}</Typography>
          {decodedData?.ErrorText ? (
            <Typography sx={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
              {String(decodedData.ErrorText)}
            </Typography>
          ) : null}
        </Box>
      ) : null}

      {decodedData ? (
        <Box sx={vinDecoderPageStyles.contentBox}>
          {columns.map((colSections, _colIndex) => (
            // eslint-disable-next-line react/no-array-index-key
            <Box key={_colIndex} sx={vinDecoderPageStyles.column}>
              {colSections.map(section => {
                const visibleFields = getVisibleFields(section, decodedData);
                return (
                  <Box key={section.title} sx={vinDecoderPageStyles.card}>
                    <Box sx={vinDecoderPageStyles.cardHeader}>{section.title}</Box>
                    {visibleFields.map(field => (
                      <Box key={field.key} sx={vinDecoderPageStyles.detailBox}>
                        <Typography sx={vinDecoderPageStyles.detailTitle}>{field.label}</Typography>
                        <Box sx={vinDecoderPageStyles.detailValue}>{field.value}</Box>
                      </Box>
                    ))}
                  </Box>
                );
              })}
            </Box>
          ))}
        </Box>
      ) : null}
    </Box>
  );
}
